
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { differenceInDays, differenceInMonths, differenceInYears, format, addDays, addMonths, addYears, subDays, subMonths, subYears } from 'date-fns';

type DateResult = {
  days: number;
  months: number;
  years: number;
  totalDays: number;
};

const DateCalculator = () => {
  const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [baseDate, setBaseDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [daysToAdd, setDaysToAdd] = useState<number>(0);
  const [monthsToAdd, setMonthsToAdd] = useState<number>(0);
  const [yearsToAdd, setYearsToAdd] = useState<number>(0);
  const [resultDate, setResultDate] = useState<string>('');
  const [dateDifference, setDateDifference] = useState<DateResult | null>(null);

  const calculateDateDifference = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return;
    }

    const totalDays = differenceInDays(end, start);
    const months = differenceInMonths(end, start);
    const years = differenceInYears(end, start);
    const remainingMonths = months % 12;
    const remainingDays = totalDays - (years * 365 + remainingMonths * 30); // Approximation

    setDateDifference({
      days: remainingDays,
      months: remainingMonths,
      years,
      totalDays
    });
  };

  const calculateNewDate = () => {
    if (!baseDate) return;

    const base = new Date(baseDate);
    if (isNaN(base.getTime())) {
      return;
    }

    let result = new Date(base);
    
    if (yearsToAdd) result = addYears(result, yearsToAdd);
    if (monthsToAdd) result = addMonths(result, monthsToAdd);
    if (daysToAdd) result = addDays(result, daysToAdd);

    setResultDate(format(result, 'yyyy-MM-dd'));
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="difference">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="difference">Date Difference</TabsTrigger>
          <TabsTrigger value="calculate">Date Calculator</TabsTrigger>
        </TabsList>
        
        <TabsContent value="difference" className="space-y-4 pt-4">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
              
              <Button onClick={calculateDateDifference} className="w-full">
                Calculate Difference
              </Button>

              {dateDifference && (
                <div className="mt-4 p-4 border rounded-md bg-slate-50">
                  <h3 className="font-semibold text-lg mb-2">Result:</h3>
                  <div className="space-y-1">
                    <p><span className="font-medium">Years:</span> {dateDifference.years}</p>
                    <p><span className="font-medium">Months:</span> {dateDifference.months}</p>
                    <p><span className="font-medium">Days:</span> {dateDifference.days}</p>
                    <p className="pt-2 border-t mt-2">
                      <span className="font-medium">Total days:</span> {dateDifference.totalDays}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="calculate" className="space-y-4 pt-4">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="baseDate">Base Date</Label>
                <Input
                  id="baseDate"
                  type="date"
                  value={baseDate}
                  onChange={(e) => setBaseDate(e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="days">Days</Label>
                  <Input
                    id="days"
                    type="number"
                    value={daysToAdd || ""}
                    onChange={(e) => setDaysToAdd(parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="months">Months</Label>
                  <Input
                    id="months"
                    type="number"
                    value={monthsToAdd || ""}
                    onChange={(e) => setMonthsToAdd(parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="years">Years</Label>
                  <Input
                    id="years"
                    type="number"
                    value={yearsToAdd || ""}
                    onChange={(e) => setYearsToAdd(parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              
              <Button onClick={calculateNewDate} className="w-full">
                Calculate Date
              </Button>

              {resultDate && (
                <div className="mt-4 p-4 border rounded-md bg-slate-50">
                  <h3 className="font-semibold text-lg mb-2">Result:</h3>
                  <p><span className="font-medium">New Date:</span> {format(new Date(resultDate), 'MMMM d, yyyy')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DateCalculator;
